import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    FormControl,
    FormControlProps,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    TextFieldProps,
    Typography
} from '@mui/material';
import { LanguagesResponse } from '@/models/company';
import { useLoader } from '@/contexts/loader/LoaderContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import UsersAPIServiceV2 from '@/services/UserServiceV2';
import NotificationAPIServiceV2 from '@/services/NotificationServiceV2';

export const EmailsTab = () => {
    const [languages, setLanguages] = useState<LanguagesResponse>();
    const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
    const [error, setError] = useState<string>();
    const [email, setEmail] = useState<string>('');

    const { setLoading } = useLoader();
    const { backendErrorHandler } = useErrorHandling();
    const { setAlert } = useNotificationsSnackbar();
    const commonProps: TextFieldProps = {
        margin: 'dense',
        fullWidth: true,
        variant: 'standard'
    };

    const fetchLanguages = useCallback(() => {
        setLoading(true);
        UsersAPIServiceV2.getInstance()
            .getSupportedLanguages()
            .then((response: any) => {
                setLanguages(response.data);
            })
            .catch(
                backendErrorHandler(undefined, () => {
                    setError('Could not load language options');
                })
            )
            .finally(() => {
                setLoading(false);
            });
    }, [setLoading, backendErrorHandler]);

    const onClick = () => {
        if (!selectedLanguage || !email) {
            setAlert('Please provide an email and selected language', 'error');
            return;
        }

        setLoading(true);
        NotificationAPIServiceV2.getInstance()
            .sendAllEmails({ email: email, language: selectedLanguage })
            .then(() => {
                setAlert('Emails sent successfully', 'success');
            })
            .catch(
                backendErrorHandler(undefined, () => {
                    setError('Could not send emails');
                })
            )
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchLanguages();
    }, [fetchLanguages]);

    return (
        <>
            <Typography variant="h6" mb={2}>
                Email settings
            </Typography>
            <Typography variant="body2" mb={3}>
                Use the form below to send test emails to the provided
                recipient.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            {languages?.languages && (
                <>
                    <TextField
                        {...commonProps}
                        label="Recipient email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                    />

                    <FormControl {...(commonProps as FormControlProps)}>
                        <InputLabel>Select language</InputLabel>
                        <Select
                            variant="standard"
                            value={selectedLanguage}
                            label="Select language"
                            onChange={(e) =>
                                setSelectedLanguage(e.target.value)
                            }
                        >
                            {languages?.languages.map((language) => (
                                <MenuItem
                                    key={language.code}
                                    value={language.code}
                                >
                                    {language.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        sx={{ marginTop: '1rem' }}
                        variant="contained"
                        onClick={onClick}
                    >
                        Send
                    </Button>
                </>
            )}
        </>
    );
};
